import {observable, computed, runInAction, action} from 'mobx';
import {getIn} from '../utils';
import * as constants from './const';
import createSelector, {LruCacheObject} from 're-reselect';

class SeasonPeriod {
  constructor(content) {
    this.content = content;
  }

  @computed get startDate() {
    return getIn(this.content, ['StartDate']);
  }

  @computed get endDate() {
    return getIn(this.content, ['EndDate']);
  }  
}

class Season {
  constructor(content) {
    this.content = content;
  }

  @computed get id() {
    return getIn(this.content, ['Id']);
  }

  @computed get name() {
    return getIn(this.content, ['Name']);
  }

  @computed get periods() {
    return getIn(this.content, ['SeasonFlatPeriod'], []).map(o => {
      return new SeasonPeriod(o);
    })
  }
}

class PickupDropoffSchedulePeriod {
  @observable _content = {};

  constructor(content) {
    this._content = content;
  }
  
  @computed get days() {
    let days = [];
    if(this._content) {
      days = [
        this._content.Monday && "mon",
        this._content.Tuesday && "tue",
        this._content.Wednesday && "wed",
        this._content.Thursday && "thu",
        this._content.Friday && "fri",
        this._content.Saturday && "sat",
        this._content.Sunday && "sun",
      ].filter(o => o);
    }
    return days;
  }

  @computed get timeFrom() {
    return getIn(this._content, ['From']);
  }

  @computed get timeTo() {
    return getIn(this._content, ['To']);
  }

}

class PickupSchedule {
  @observable _content = {};

  constructor(content) {
    this._content = content;
  }

  @computed get periods() {
    return getIn(this._content, ['PickupSchedulePeriod'], [])
      .map(o => new PickupDropoffSchedulePeriod(o));
  }

  @computed get id() {
    return getIn(this._content, ['Id']);
  }
}

class DropoffSchedule {
  @observable _content = {};

  constructor(content) {
    this._content = content;
  }

  @computed get periods() {
    return getIn(this._content, ['DropoffSchedulePeriod'], [])
      .map(o => new PickupDropoffSchedulePeriod(o));
  }

  @computed get id() {
    return getIn(this._content, ['Id']);
  }
}

class Offer {
  @observable content = {};

  constructor(content, organization, businessUnit, rentalObject) {
    this.content = content;
    this.organization = organization;
    this.businessUnit = businessUnit;
    this.rentalObject = rentalObject;
  }

  @computed get name() {
    return this.content.Name;
  }

  @computed get id() {
    return getIn(this.content, ['Id']); 
  }

  @computed get price() {
    return getIn(this.content, ['FlatPrice']) || getIn(this.content, ['OfferPrice'])
  }

  @computed get pricePerTimeUnit() {
    return getIn(this.content, ['PricePerTimeUnit']);
  }

  @computed get timeUnitId() {
    return this.content.refAllotmentTimeUnitId;
  }

  @computed get timeUnit() {
    return this.organization.timeUnitById(this.timeUnitId).name;
  }

  @computed get timeUnitQuantity() {
    return getIn(this.content, ['TimeUnitQuantity']);
  }

  @computed get seasonId() {
    return getIn(this.content, ['refSeasonId']);
  }

  @computed get season() {
    let season = this.rentalObject.seasons.find(o => {
      return o.id === this.seasonId;
    }) || new Season({});
    return season;
  }

  @computed get description() {
    return getIn(this.content, ['Description']);
  }

  @computed get refPickupScheduleId() {
    return getIn(this.content, ['refPickupScheduleId']);
  }

  @computed get refDropoffScheduleId() {
    return getIn(this.content, ['refDropoffScheduleId']);
  }

  @computed get pickupSchedule() {
    return this.rentalObject.pickupScheduleItems.find(
      o => o.id === this.refPickupScheduleId);
  }

  @computed get dropoffSchedule() {
    return this.rentalObject.dropoffScheduleItems.find(
      o => o.id === this.refDropoffScheduleId);
  }

  @computed get prolongation() {
    if(!this.content.ProlongationName) return null;
    return {
      name: getIn(this.content, ['ProlongationName']),
      //ticks : getIn(this.content, ['ProlongationTicks']),
      tuName: getIn(this.content, ['AllotmentTimeUnit_Name']),
      tuQuantity : getIn(this.content, ['ProlongationTimeUnitQuantity']),
      offerPrice : getIn(this.content, ['ProlongationOfferPrice']),
      currency: this.businessUnit.currencyName
    }
  }
}

class Consumable {
  @observable content = {};

  constructor(content) {
    this.content = content;
  }

  @computed get name() {
    return getIn(this.content, ['Consumable_Name']);
  }

  @computed get minQuantity() {
    return getIn(this.content, ['MinQuantity']);
  }

  @computed get unitName() {
    return getIn(this.content, ['Consumable_Unit']);
  }

  @computed get pricePerUnit() {
    return getIn(this.content, ['pricePerUnit']);
  }

  @computed get offers() {
    return getIn(this.content, ['RentalObjectConsumableOffer'], []).map(o => {
      return new ConsumableOffer(o);
    });
  }

  @computed get sortedOffers() {
    let offers = [...this.offers];
    // offers.sort((a,b) => {
    //   if(a.name > b.name) return 1;
    //   if(a.name < b.name) return -1;
    //   if(a.freeUnitQuantity > b.freeUnitQuantity) return 1;
    //   if(a.freeUnitQuantity < b.freeUnitQuantity) return -1;
    //   return 0;
    // })
    return offers;
  } 

}

class ConsumableOffer {
  @observable content = {};

  constructor(content) {
    this.content = content;
  }

  @computed get name() {
    return getIn(this.content, ['RentalObjectOffer_Name']);
  }

  @computed get freeUnitQuantity() {
    return getIn(this.content, ['FreeUnitQuantity']);
  }
}

class Attachment {
  @observable content = {};

  constructor(content, organization) {
    this.content = content;
    this.organization = organization;
  }

  @computed get id() {
    return getIn(this.content, ['Id']);
  }

  @computed get name() {
    return getIn(this.content, ['Name']);
  }  

  @computed get attachmentTypeId() {
    return getIn(this.content, ['refRentalObjectAttachmentTypeId']);
  }

  @computed get isImage() {
    return this.attachmentTypeId === constants.ATTACHMENT_TYPE.PICTURE;
  }

  @computed get thumbnailUrl() {
    return `/lessors/DBImg.ashx?id=${this.id}`
  }

  @computed get pictureUrl() {
    return `/lessors/DBImg.ashx?id=${this.id}`
  }
}

export class Property {
  @observable content = {};

  constructor(content) {
    this.content = content;
  }

  @computed get name() {
    return getIn(this.content, ['RentalParameter_Name']);
  }

  @computed get typeName() {
    return getIn(this.content, ['DataType_Name']);
  }

  @computed get value() {
    const dtype = this.typeName;
    const vkey = `${dtype}Value`;
    const value = getIn(this.content, [vkey]);
    switch(dtype) {
      case 'Boolean':
        return value ? 'Ja' : 'Nein';
      case 'Enum':
        return getIn(this.content, [`${vkey}_Name`]);
      default:
        return value;
    }
  }
}

class Accessory {
  @observable content;

  constructor(content) {
    this.content = content;
  }

  @computed get name() {
    return getIn(this.content, ['AccessoryRentalObject_Name']);
  }

  @computed get description() {
    return getIn(this.content, ['AccessoryRentalObject_Description']);
  }

  @computed get offers() {
    return getIn(this.content, ['AccessoryRentalObjectOffer'], []).map(o => {
      return new AccessoryOffer(o);
    });
  }
}

class AccessoryOffer {
  constructor(content) {
    this.content = content;
  }

  @computed get name() {
    return getIn(this.content, ['DisplayName']);
  }

  @computed get price() {
    return getIn(this.content, ['FlatPrice']) || getIn(this.content, ['OfferPrice'])
  }  
}

export class RentalObject {
  @observable content = {};
  
  constructor(api, organization, businessUnit) { 
    this.api = api;
    this.organization = organization;
    this.businessUnit = businessUnit;
  }

  @computed get data() {
    return this.content;
  }

  @computed get name() {
    return getIn(this.content, ['Name']);
  }

  @computed get description() {
    return getIn(this.content, ['Description']);
  }

  @computed get shortDescription() {
    return getIn(this.content, ['ShortDescription']);
  }

  @computed get businessUnitName() {
    return getIn(this.content, ['BusinessUnit_Name']);
  }

  @computed get notBookableOnline() {
    return getIn(this.content, ['NotBookableOnline']);
  }

  @computed get notesRequest() {
    return getIn(this.content, ['NotesRequest']);
  }

  @computed get notesRequest2() {
    return getIn(this.content, ['NotesRequest2']);
  }

  @computed get timeUnitForStartingPrice() {
    return getIn(this.content, ['PriceFromAllotmentTimeUnit_Name']);
  }

  @computed get startingPrice() {
    let offers = [...(this.offers || [])];
    offers.sort((a, b) => {
      return parseFloat(a.pricePerTimeUnit) - parseFloat(b.pricePerTimeUnit);
    })
    return getIn(offers, [0, 'pricePerTimeUnit']);
  }

  @computed get resultPriceFrom() {
    return getIn(this.content, ['ResultPriceFrom'], null);
  }

  @computed get bestOffer() {
    let offers = this.offers || [];
    if(offers.length === 0) return {};
    return offers[offers.length - 1];
  }

  @computed get properties() {
    return getIn(this.content, ['RentalObjectRentalParameterValue'], []).map(o => {
      return new Property(o);
    })
  }

  @computed get consumables() {
    return getIn(this.content, ['RentalObjectConsumable'], []).map(o => {
      return new Consumable(o);
    })
  }

  @computed get attachments() {
    return getIn(this.content, ['RentalObjectAttachment'], []).map(o => {
      return new Attachment(o, this.organization);
    })
  }

  @computed get pictureAttachments() {
    return this.attachments.filter(o => o.isImage);
  }

  @computed get firstImageId() {
    if(this.pictureAttachments.length > 0) {
      return this.pictureAttachments[0].id;
    }
  }

  @computed get thumbnailUrl() {
    if(this.firstImageId) {
      return `/lessors/DBImg.ashx?id=${this.firstImageId}`
    } else {
      return
    }
  }

  @computed get pickupScheduleItems() {
    return getIn(this.content, ['PickupSchedule'], [])
      .map(o => new PickupSchedule(o));
  } 

  @computed get dropoffScheduleItems() {
    return getIn(this.content, ['DropoffSchedule'], [])
      .map(o => new DropoffSchedule(o));
  } 

  @action async fetch({orgId, objId}) {
    if(this._loadingPromise) {
      return this._loadingPromise;
    }
    try{
      if(objId !== this.id) {
        this._loadingPromise = this.api.getRentalObject({
          organizationId: orgId,
          objectId: objId,
        });
        this.content = await this._loadingPromise;
      }
    }
    finally {
      this._loadingPromise = undefined;
    }
  }

  cFetch = createSelector(
    [p => p],
    (p) => this.fetch(p)
  )(
    ({orgId, objId}) => 
      `${orgId}@${objId}`,
    {cacheObject: new LruCacheObject({cacheSize: 1}), }
  );    

  @computed get offers() {
    return getIn(this.data, ['RentalObjectOffer'], []).map(o => {
      return new Offer(o, this.organization, this.businessUnit, this);
    })
  }

  @computed get seasons() {
    return getIn(this.data, ['Seasons'], []).map(o => {
      return new Season(o);
    })
  }

  @computed get sortedOffers() {
    let offers = [...this.offers];
    // offers.sort((a,b) => {
    //   if(a.name > b.name) return 1;
    //   if(a.name < b.name) return -1;
    //   if(a.timeUnitQuantity > b.timeUnitQuantity) return 1;
    //   if(a.timeUnitQuantity < b.timeUnitQuantity) return -1;
    //   return 0;
    // })
    return offers;
  }  

  @computed get accessories() {
    return getIn(this.content, ['RentalObjectAccessory'], []);
  }

  @computed get accessoriesWr() {
    return this.accessories.map(o => {
      return new Accessory(o);
    })
  }

  @computed get businessUnitId() {
    return getIn(this.content, ['refBusinessUnitId']);
  }

  @computed get services() {
    return getIn(this.content, ['RentalObjectService'], []);
  }

}


// WEBPACK FOOTER //
// ./src/model/RentalObject.js